import React, { memo, useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components/macro";
import {
  Desktop,
  DesktopDown,
  DesktopLarge,
  DesktopLargeDown,
  device,
} from "theme/Breakpoints";
import { noSelect } from "theme/GlobalElements";
import {
  fetchProductDetails,
  selectFetchedProduct,
  selectFetchedProductLoading,
  setFetchedProduct,
} from "./productDetailsSlice";
import HotColdDescription from "./components/HotColdDescription";
import TechnicalDescription from "./components/TechnicalDescription";
import { setBreadcrumbTitle } from "features/breadcrumb/breadcrumbSlice";
import FastOrder from "components/FastOrder";
import ProductMoreInfo from "components/ProductMoreInfo/ProductMoreInfo";
import Labels from "components/Label/Labels";
import Preloader from "components/Preloader/Preloader";
import ProductMoreInfoDesktop from "components/ProductMoreInfo/ProductMoreInfoDesktop";
import PriceAndButtons from "./components/PriceAndButtons";
import ProductImage from "./components/ProductImage";
import { getLabels } from "components/Product/utils";
import MultisplitOptions from "components/multisplitOptions";
import { Product } from "features/ProductReel/types";
import { EventService } from "utils/EventService";
import { isEmpty } from "features/utils";
import HeadMeta from "./components/HeadMeta";
import BestPrice from "components/BestPrice";

const ProductDetails = () => {
  const dispatch = useDispatch();
  const { alias } = useParams();

  const fetchedProduct = useSelector(selectFetchedProduct);
  const fetchedProductLoading = useSelector(selectFetchedProductLoading);

  const handleBackButton = (productId: any) => {
    EventService.emitEvent("backButtonFromDetails", {
      productId: productId,
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    return () => {
      dispatch(setFetchedProduct({} as Product));
    };
  }, []);

  useEffect(() => {
    dispatch(fetchProductDetails(alias));
  }, [alias]);

  useLayoutEffect(() => {
    if (!isEmpty(fetchedProduct)) {
      dispatch(setBreadcrumbTitle(fetchedProduct?.title));
      window.addEventListener("popstate", () =>
        handleBackButton(fetchedProduct._id)
      );
    }
    return () => {
      dispatch(setBreadcrumbTitle(""));
    };
  }, [fetchedProduct]);

  const labels = getLabels(fetchedProduct);

  return fetchedProductLoading || Object.keys(fetchedProduct).length === 0 ? (
    <Preloader />
  ) : (
    <>
      <HeadMeta item={fetchedProduct} />
      <ProductDetailsContainer>
        <ProductImageContainer>
          <LabelsContainer>
            <Labels labels={labels} />
          </LabelsContainer>
          {Array.isArray(fetchedProduct?.images) && fetchedProduct.images && (
            <ProductImage images={fetchedProduct.images} />
          )}
        </ProductImageContainer>
        <ContentContainer>
          <TitleRatingContainer>
            <Title className="text-md">{fetchedProduct?.title}</Title>
            {/* <Rating>
              <Stars>
                {[1, 2, 3, 4, 5].map((i) => {
                  return (
                    <MdStar
                      className="text-sm"
                      key={i}
                      style={{
                        width: 16,
                        height: 16,
                        fill: "#FFC107",
                      }}
                    />
                  );
                })}{" "}
                <ReviewsText>Отзиви(4)</ReviewsText>
              </Stars>
              <WriteReview className="text-sm">Напиши отзив</WriteReview>
            </Rating> */}
          </TitleRatingContainer>
          <PriceAndButtons fetchedProduct={fetchedProduct} />
          <TechnicalDescription fetchedProduct={fetchedProduct} />

          <DesktopLarge>
            <HotColdDescription fetchedProduct={fetchedProduct} />
          </DesktopLarge>
        </ContentContainer>
      </ProductDetailsContainer>

      <DesktopLargeDown>
        <HotColdDescription fetchedProduct={fetchedProduct} />
      </DesktopLargeDown>

      <DesktopDown>
        <ProductMoreInfo fetchedProduct={fetchedProduct} />
      </DesktopDown>

      <Desktop>
        <ProductMoreInfoDesktop fetchedProduct={fetchedProduct} />
      </Desktop>
      <BestPrice />
      <FastOrder item={fetchedProduct} />
      <MultisplitOptions items={fetchedProduct?.internal_bodies} />
    </>
  );
};

export const ProductDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  padding: 0 20px;
  @media only screen and (${device.tablet}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
`;
export const ProductImageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 180px;

  @media only screen and (${device.tablet}) {
    width: 350px;
    height: 260px;
    margin-right: 10px;
  }

  @media only screen and (${device.laptop}) {
    width: 472px;
    height: 354px;
    margin-right: 20px;
  }

  @media only screen and (${device.laptopL}) {
    width: 640px;
    height: 480px;
    margin-right: 20px;
  }
`;

const LabelsContainer = styled.div`
  position: absolute;
  top: 5px;
  left: 5px;
  display: flex;
  flex-flow: row wrap;
  overflow: visible;
  width: 100%;
  z-index: 2;

  @media only screen and (${device.tablet}) {
    top: 10px;
    left: 10px;
  }
`;

export const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-content: flex-start;

  @media only screen and (${device.tablet}) {
    align-items: flex-start;
    flex-flow: row wrap;
  }
`;

export const TitleRatingContainer = styled.div`
  flex: 1;
  align-items: center;
  margin-bottom: 12px;

  @media only screen and (${device.tablet}) {
    flex: 0 0 100%;
  }
`;

export const Title = styled.h1`
  ${noSelect};
  flex: 1;
  text-align: center;
  font-weight: bold;
  margin-bottom: 5px;
  color: black;

  @media only screen and (${device.tablet}) {
    text-align: left;
  }
`;
// const Rating = styled.div`
//   display: flex;
//   flex-flow: row nowrap;
//   justify-content: center;
//   align-items: center;
//   flex: 1;

//   @media only screen and (${device.tablet}) {
//     justify-content: flex-start;
//   }
// `;

// const Stars = styled.div`
//   display: flex;
//   align-items: center;
//   flex-direction: row;
//   margin-right: 10px;

//   span {
//     margin-left: 5px;
//   }

//   @media only screen and (${device.tablet}) {
//     span {
//       margin-left: 5px;
//     }
//   }
// `;

// const ReviewsText = styled(DefaultText)`
//   margin-left: 5px;
// `;
// const WriteReview = styled(DefaultText)`
//   font-weight: 500;
// `;

export default memo(ProductDetails);
